import { useState, useEffect, useRef } from 'react';

const utmFeatures = [
  { title: 'ファイアウォール', desc: '外部からの不正な通信を自動でブロック', color: 'bg-red-50 border-red-100' },
  { title: 'ウイルス対策', desc: 'ネットワーク全体でウイルスを検知・排除', color: 'bg-orange-50 border-orange-100' },
  { title: '不正アクセス防止', desc: '侵入の試みをリアルタイムで検知・遮断', color: 'bg-amber-50 border-amber-100' },
  { title: 'Webフィルタリング', desc: '危険なサイトへのアクセスを自動でブロック', color: 'bg-sky-50 border-sky-100' },
  { title: 'VPN', desc: '在宅勤務・外出先からも安全に社内接続', color: 'bg-teal-50 border-teal-100' },
];

const threats = ['ウイルス', '不正アクセス', 'フィッシング', 'ランサムウェア'];

const humanRisks = [
  { title: '不正ログイン', desc: 'ID・パスワードが盗まれて勝手にログインされるリスク' },
  { title: '情報入力による漏洩', desc: 'フィッシングサイトに情報を入力してしまうリスク' },
  { title: 'AI利用による情報流出', desc: 'AIツールに機密情報を入力することで外部に漏れるリスク' },
  { title: 'フィッシング詐欺', desc: '偽のメールに誘導されて被害を受けるリスク' },
];

const threeLayers = [
  {
    step: '01',
    title: 'UTM（入口対策）',
    desc: 'ネットワークの出入り口を守る。外部からの攻撃を最初の防衛ラインでブロック。',
    color: 'bg-sky-600',
  },
  {
    step: '02',
    title: 'セキュアブラウザ（操作制御）',
    desc: 'パソコン上での危険な操作を制御。情報の入力・コピー・保存を管理してリスクを低減。',
    color: 'bg-teal-600',
  },
  {
    step: '03',
    title: '社員教育',
    desc: '人のミスを減らす。フィッシングやAIリスクなど「人が起こす」被害に対応する最重要対策。',
    color: 'bg-slate-700',
  },
];

const SolutionSection: React.FC = () => {
  const [mode, setMode] = useState<'before' | 'after'>('before');
  const [animStep, setAnimStep] = useState(0);
  const [visibleFeatures, setVisibleFeatures] = useState<boolean[]>(Array(5).fill(false));
  const featRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const id = setInterval(() => {
      setAnimStep((s) => (s + 1) % 4);
    }, 800);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    const el = featRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting) {
          utmFeatures.forEach((_, i) => {
            setTimeout(() => {
              setVisibleFeatures((prev) => {
                const next = [...prev];
                next[i] = true;
                return next;
              });
            }, i * 120);
          });
          observer.disconnect();
        }
      },
      { threshold: 0.2 }
    );
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* ============ UTMとは？ビジュアル図解 ============ */}
      <section id="solution" className="py-24 bg-white overflow-hidden">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-12">
            <span className="inline-block text-sky-600 text-sm font-bold tracking-widest uppercase mb-4">
              解決策
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 leading-tight">
              その対策、UTMで<br className="md:hidden" />まとめて守れます
            </h2>
            <p className="mt-4 text-slate-500 text-base max-w-xl mx-auto leading-relaxed">
              UTMとは「複数のセキュリティ機能を1台に集約した機器」です。<br />
              導入前後でオフィスのネットワークが劇的に変わります。
            </p>
          </div>

          {/* Before / After toggle */}
          <div className="flex justify-center mb-8">
            <div className="inline-flex bg-slate-100 rounded-full p-1">
              <button
                onClick={() => setMode('before')}
                className={`whitespace-nowrap px-8 py-2.5 rounded-full text-sm font-bold transition-all duration-300 cursor-pointer ${
                  mode === 'before'
                    ? 'bg-red-500 text-white'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                UTMなし（今の状態）
              </button>
              <button
                onClick={() => setMode('after')}
                className={`whitespace-nowrap px-8 py-2.5 rounded-full text-sm font-bold transition-all duration-300 cursor-pointer ${
                  mode === 'after'
                    ? 'bg-sky-600 text-white'
                    : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                UTMあり（導入後）
              </button>
            </div>
          </div>

          {/* ===== DIAGRAM ===== */}
          <div className={`rounded-2xl p-8 md:p-12 transition-colors duration-500 ${
            mode === 'before' ? 'bg-red-50 border border-red-200' : 'bg-sky-50 border border-sky-200'
          }`}>
            {/* Title */}
            <div className="text-center mb-8">
              {mode === 'before' ? (
                <div className="inline-flex items-center gap-2 bg-red-100 text-red-700 px-5 py-2.5 rounded-full text-sm font-bold">
                  危険：ネットワークが無防備な状態
                </div>
              ) : (
                <div className="inline-flex items-center gap-2 bg-sky-100 text-sky-700 px-5 py-2.5 rounded-full text-sm font-bold">
                  安全：UTMがすべての脅威を水際でブロック
                </div>
              )}
            </div>

            {/* Network diagram */}
            <div className="flex items-stretch justify-center gap-0 md:gap-2 overflow-x-auto pb-2">

              {/* === Internet / Threats box === */}
              <div className="flex flex-col items-center gap-3 min-w-[110px]">
                <div className={`w-20 h-20 flex items-center justify-center rounded-2xl border-2 ${
                  mode === 'before' ? 'bg-red-100 border-red-300' : 'bg-slate-100 border-slate-300'
                }`}>
                  <span className={`text-xs font-bold ${mode === 'before' ? 'text-red-500' : 'text-slate-500'}`}>WEB</span>
                </div>
                <p className="text-xs font-semibold text-center text-slate-600">インターネット</p>
                <div className="grid grid-cols-2 gap-1.5 mt-1">
                  {threats.map((t) => (
                    <div key={t} className="flex flex-col items-center gap-0.5">
                      <div className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                        mode === 'before' ? 'bg-red-200' : 'bg-slate-200'
                      }`}>
                        <span className={`text-[9px] font-bold ${mode === 'before' ? 'text-red-600' : 'text-slate-400'}`}>!</span>
                      </div>
                      <span className="text-slate-500 text-[9px] text-center leading-tight">{t}</span>
                    </div>
                  ))}
                </div>
              </div>

              {/* === Arrow (threat flow) === */}
              <div className="flex items-center justify-center px-1 md:px-2 min-w-[60px] md:min-w-[80px] relative">
                <div className={`w-full h-2 rounded-full relative overflow-visible ${
                  mode === 'before' ? 'bg-red-300' : 'bg-slate-300'
                }`}>
                  {[0, 0.6, 1.2].map((_, i) => (
                    <span
                      key={i}
                      className={`absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full ${
                        mode === 'before' ? 'bg-red-500' : 'bg-slate-400'
                      }`}
                      style={{
                        left: `${((animStep + i) % 4) * 30}%`,
                        transition: 'left 0.4s linear',
                      }}
                    />
                  ))}
                </div>
              </div>

              {/* === UTM Box (only in "after" mode) === */}
              {mode === 'after' && (
                <>
                  <div className="flex flex-col items-center gap-3 min-w-[120px]">
                    <div className="relative">
                      <div className="w-24 h-24 flex items-center justify-center rounded-2xl border-2 bg-sky-600 border-sky-700">
                        <span className="text-white font-bold text-sm">UTM</span>
                      </div>
                      <span className="absolute -top-2 -right-2 bg-emerald-500 text-white text-[10px] font-bold px-1.5 py-0.5 rounded-full">
                        BLOCK
                      </span>
                    </div>
                    <p className="text-xs font-bold text-sky-700 text-center">UTM</p>
                    <div className="flex flex-col gap-1 w-full">
                      {['ファイアウォール', 'ウイルス対策', 'Web制限'].map((f) => (
                        <div key={f} className="flex items-center gap-1.5 bg-sky-100 rounded-lg px-2 py-1">
                          <span className="text-sky-700 text-[10px] font-semibold whitespace-nowrap">{f}</span>
                        </div>
                      ))}
                    </div>
                  </div>

                  {/* After-UTM arrow (safe) */}
                  <div className="flex items-center justify-center px-1 md:px-2 min-w-[60px] md:min-w-[80px] relative">
                    <div className="w-full h-2 rounded-full bg-emerald-300 relative overflow-visible">
                      {[0, 0.6, 1.2].map((_, i) => (
                        <span
                          key={i}
                          className="absolute top-1/2 -translate-y-1/2 w-3 h-3 rounded-full bg-emerald-500"
                          style={{
                            left: `${((animStep + i) % 4) * 30}%`,
                            transition: 'left 0.4s linear',
                          }}
                        />
                      ))}
                    </div>
                  </div>
                </>
              )}

              {/* === Office network === */}
              <div className="flex flex-col items-center gap-3 min-w-[110px]">
                <div className={`w-20 h-20 flex items-center justify-center rounded-2xl border-2 transition-colors duration-500 ${
                  mode === 'before'
                    ? 'bg-red-100 border-red-300 animate-pulse'
                    : 'bg-emerald-100 border-emerald-300'
                }`}>
                  <span className={`text-xs font-bold ${
                    mode === 'before' ? 'text-red-500' : 'text-emerald-600'
                  }`}>社内</span>
                </div>
                <p className="text-xs font-semibold text-center text-slate-600">社内ネットワーク</p>
                <div className="grid grid-cols-3 gap-1 mt-1">
                  {['PC', '複合機', 'SP'].map((device) => (
                    <div key={device} className={`w-8 h-8 flex items-center justify-center rounded-lg ${
                      mode === 'before' ? 'bg-red-100' : 'bg-emerald-100'
                    }`}>
                      <span className={`text-[8px] font-bold ${mode === 'before' ? 'text-red-500' : 'text-emerald-600'}`}>{device}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Status message */}
            <div className={`mt-8 rounded-xl px-6 py-4 flex items-start gap-4 ${
              mode === 'before' ? 'bg-red-100' : 'bg-emerald-100'
            }`}>
              <div>
                <p className={`font-bold text-sm ${mode === 'before' ? 'text-red-800' : 'text-emerald-800'}`}>
                  {mode === 'before'
                    ? 'ウイルス・不正アクセス・ランサムウェアが社内ネットワークに直接侵入できる状態です'
                    : 'UTMがすべての脅威を入口でブロック。社内ネットワークは常に安全な状態を維持します'}
                </p>
                <p className={`text-xs mt-1 leading-relaxed ${mode === 'before' ? 'text-red-600' : 'text-emerald-600'}`}>
                  {mode === 'before'
                    ? '→ 被害が出てからでは遅い。「うちは大丈夫」という時代は終わっています。'
                    : '→ 機能は自動で動作。IT担当者がいなくても24時間365日、社内を守り続けます。'}
                </p>
              </div>
            </div>
          </div>

          {/* CTA to switch mode */}
          {mode === 'before' && (
            <div className="text-center mt-6">
              <button
                onClick={() => setMode('after')}
                className="whitespace-nowrap inline-flex items-center gap-2 bg-sky-600 hover:bg-sky-500 text-white font-bold px-8 py-3.5 rounded-full text-sm transition-all duration-200 cursor-pointer"
              >
                UTM導入後の状態を見る
              </button>
            </div>
          )}
        </div>
      </section>

      {/* ============ UTMの機能 5つ ============ */}
      <section className="py-20 bg-slate-50">
        <div className="max-w-5xl mx-auto px-6" ref={featRef}>
          <div className="text-center mb-12">
            <span className="inline-block text-sky-600 text-sm font-bold tracking-widest uppercase mb-4">
              機能詳細
            </span>
            <h2 className="text-2xl md:text-3xl font-bold text-slate-800 leading-tight">
              UTM 1台でカバーできる<br />5つのセキュリティ機能
            </h2>
            <p className="mt-3 text-slate-500 text-sm max-w-lg mx-auto leading-relaxed">
              バラバラに対策するより、低コストで包括的に守れるのがUTMの最大のメリットです。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
            {utmFeatures.map((f, i) => (
              <div
                key={f.title}
                className={`bg-white rounded-2xl p-5 flex flex-col items-center text-center gap-3 border transition-all duration-500 ${
                  visibleFeatures[i]
                    ? 'opacity-100 translate-y-0'
                    : 'opacity-0 translate-y-6'
                } ${f.color.split(' ')[1]}`}
                style={{ transitionDelay: `${i * 80}ms` }}
              >
                <div>
                  <p className="font-bold text-slate-700 text-sm mb-1">{f.title}</p>
                  <p className="text-slate-500 text-xs leading-relaxed">{f.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="mt-8 bg-sky-600 text-white rounded-2xl px-8 py-6 flex flex-col md:flex-row items-center gap-5">
            <div className="text-center md:text-left">
              <p className="font-bold text-lg">これら5つをまとめて1台で実現できるのがUTMです</p>
              <p className="text-sky-200 text-sm mt-1 leading-relaxed">
                個別に導入すると費用・管理の手間が何倍にもなります。UTMなら導入後は基本自動で動作します。
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============ UTMだけでは防げないリスク ============ */}
      <section id="differentiation" className="py-24 bg-white">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block text-amber-600 text-sm font-bold tracking-widest uppercase mb-4">
              重要な注意点
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 leading-tight">
              UTMだけでは防げない<br className="md:hidden" />
              リスクもあります
            </h2>
            <p className="mt-4 text-slate-500 text-base max-w-xl mx-auto leading-relaxed">
              UTMはネットワーク入口を守りますが、「人の操作」によるリスクには対応できません。
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 mb-12">
            {humanRisks.map((r) => (
              <div key={r.title} className="bg-amber-50 border border-amber-100 rounded-xl p-6">
                <p className="font-bold text-slate-700 text-sm mb-1">{r.title}</p>
                <p className="text-slate-500 text-xs leading-relaxed">{r.desc}</p>
              </div>
            ))}
          </div>

          <div className="bg-slate-800 rounded-2xl px-8 py-7 text-center">
            <p className="text-white text-lg font-bold">
              人の操作によるリスクはUTMでは防げない
            </p>
            <p className="text-slate-400 text-sm mt-2">
              だからこそ、UTMと合わせた「総合的なセキュリティ対策」が必要です。
            </p>
          </div>
        </div>
      </section>

      {/* ============ 3つの対策 ============ */}
      <section id="three-layers" className="py-24 bg-sky-50">
        <div className="max-w-5xl mx-auto px-6">
          <div className="text-center mb-16">
            <span className="inline-block text-sky-600 text-sm font-bold tracking-widest uppercase mb-4">
              総合提案
            </span>
            <h2 className="text-3xl md:text-4xl font-bold text-slate-800 leading-tight">
              これからのセキュリティは<br />
              <span className="text-sky-600">"3つの対策"</span>
            </h2>
            <p className="mt-4 text-slate-500 text-base max-w-xl mx-auto leading-relaxed">
              UTMだけでなく、人の操作まで含めた包括的な防衛体制が、中小企業を守る最善策です。
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {threeLayers.map((l) => (
              <div key={l.step} className="bg-white rounded-2xl overflow-hidden border border-slate-100">
                <div className={`${l.color} px-6 py-5 flex items-center gap-3`}>
                  <span className="text-white/60 text-xs font-bold tracking-widest">{l.step}</span>
                  <span className="text-white font-bold text-sm">{l.title}</span>
                </div>
                <div className="px-6 py-5">
                  <p className="text-slate-500 text-sm leading-relaxed">{l.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>
    </>
  );
};

export default SolutionSection;
